import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"

import Column from "../../../common/components/Column"
import RecruitProfileDisplayFieldGrid from "./RecruitProfileDisplayFieldGrid"
import RecruitProfileSectionCard from "./RecruitProfileSectionCard"
import {
  OBJECTION_RECOMMENDATION_PAIRS,
  buildPreviewSections,
} from "./brokerageProfileIntakeFields"
import {
  RECRUIT_PROFILE_SECTION_TITLES,
  RECRUIT_PROFILE_SELF_NESTED_TITLES,
  recruitProfileNestedPanelSx,
} from "./recruitProfileSectionStyles"

const FULL_WIDTH_LABELS = [
  "Credibility Statement",
  "Website",
  "Brokerage Name",
  ...OBJECTION_RECOMMENDATION_PAIRS.flatMap((pair) => [
    pair.objectionLabel,
    pair.recommendationLabel,
  ]),
  "Professional Bio Highlights",
  "Personal Bio Highlights",
]

function LocationSectionBody({ locationGroups }) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)

  return (
    <Column sx={{ gap: 1.5 }}>
      {locationGroups.map((group) => (
        <Box key={group.title} sx={panelSx}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              display: "block",
              mb: 1.25,
            }}
          >
            {group.title.toUpperCase()}
          </Typography>
          <RecruitProfileDisplayFieldGrid
            rows={group.rows.map(([label, value]) => ({ label, value }))}
            fullWidthLabels={FULL_WIDTH_LABELS}
          />
        </Box>
      ))}
    </Column>
  )
}

function AdditionalOfficesSectionBody({ officeBlocks }) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)

  return (
    <Column sx={{ gap: 1.25 }}>
      {officeBlocks.map((block) => (
        <Box key={block.id} sx={panelSx}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mb: block.checked && block.detailRows.length ? 1.25 : 0,
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {block.checkboxLabel.replace(/\?$/, "")}
            </Typography>
            <Chip
              label={block.checked ? "Yes" : "No"}
              size="small"
              color={block.checked ? "primary" : "default"}
              variant={block.checked ? "filled" : "outlined"}
              sx={{ height: 22, fontWeight: 600, fontSize: "0.75rem" }}
            />
          </Box>
          {block.checked && block.detailRows.length ? (
            <RecruitProfileDisplayFieldGrid
              rows={block.detailRows.map(([label, value]) => ({
                label,
                value,
              }))}
            />
          ) : null}
        </Box>
      ))}
    </Column>
  )
}

function OfficesScaleSectionBody({ section }) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)
  const scaleRows = []
  const credibilityRows = []
  for (const [label, value] of section.rows) {
    const row = { label, value }
    if (label === "No. of Agents" || label === "Years in Business") {
      scaleRows.push(row)
    } else {
      credibilityRows.push(row)
    }
  }

  return (
    <Column sx={{ gap: 1.5 }}>
      {scaleRows.length ? (
        <Box sx={panelSx}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              display: "block",
              mb: 1.25,
            }}
          >
            SCALE
          </Typography>
          <RecruitProfileDisplayFieldGrid
            rows={scaleRows}
            fullWidthLabels={FULL_WIDTH_LABELS}
          />
        </Box>
      ) : null}
      {credibilityRows.length ? (
        <Box sx={panelSx}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              display: "block",
              mb: 1.25,
            }}
          >
            CREDIBILITY
          </Typography>
          <RecruitProfileDisplayFieldGrid
            rows={credibilityRows}
            fullWidthLabels={FULL_WIDTH_LABELS}
          />
        </Box>
      ) : null}
    </Column>
  )
}

function KeyFeaturesSectionBody({ rows }) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)
  const groups = [
    { title: "Feature 1", labels: ["Key Feature 1", "Key Feature 1 Short"] },
    { title: "Feature 2", labels: ["Key Feature 2", "Key Feature 2 Short"] },
    { title: "Feature 3", labels: ["Key Feature 3", "Key Feature 3 Short"] },
  ]

  return (
    <Column sx={{ gap: 1.5 }}>
      {groups.map((group) => {
        const groupRows = rows
          .filter(([label]) => group.labels.includes(label))
          .map(([label, value]) => ({ label, value }))
        if (!groupRows.length) {
          return null
        }
        return (
          <Box key={group.title} sx={panelSx}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.4,
                display: "block",
                mb: 1.25,
              }}
            >
              {group.title.toUpperCase()}
            </Typography>
            <RecruitProfileDisplayFieldGrid
              rows={groupRows}
              fullWidthLabels={FULL_WIDTH_LABELS}
            />
          </Box>
        )
      })}
    </Column>
  )
}

function ObjectionsSectionBody({ rows }) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)

  return (
    <Column sx={{ gap: 1.5 }}>
      {OBJECTION_RECOMMENDATION_PAIRS.map((pair) => {
        const groupRows = rows
          .filter(([label]) =>
            [pair.objectionLabel, pair.recommendationLabel].includes(label)
          )
          .map(([label, value]) => ({ label, value }))
        if (!groupRows.length) {
          return null
        }
        return (
          <Box key={pair.objectionKey} sx={panelSx}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.4,
                display: "block",
                mb: 1.25,
              }}
            >
              {pair.objectionLabel.toUpperCase()}
            </Typography>
            <RecruitProfileDisplayFieldGrid
              rows={groupRows}
              fullWidthLabels={FULL_WIDTH_LABELS}
            />
          </Box>
        )
      })}
    </Column>
  )
}

function SectionBody({ section }) {
  if (
    section.title === RECRUIT_PROFILE_SECTION_TITLES.officeAddress &&
    section.locationGroups?.length
  ) {
    return <LocationSectionBody locationGroups={section.locationGroups} />
  }

  if (
    section.title === RECRUIT_PROFILE_SECTION_TITLES.additionalOffices &&
    section.officeBlocks?.length
  ) {
    return <AdditionalOfficesSectionBody officeBlocks={section.officeBlocks} />
  }

  if (section.title === RECRUIT_PROFILE_SECTION_TITLES.officesScale) {
    return <OfficesScaleSectionBody section={section} />
  }

  if (section.title === "Key features") {
    return <KeyFeaturesSectionBody rows={section.rows} />
  }

  if (section.title === RECRUIT_PROFILE_SECTION_TITLES.commonObjections) {
    return <ObjectionsSectionBody rows={section.rows} />
  }

  return (
    <RecruitProfileDisplayFieldGrid
      rows={section.rows.map(([label, value]) => ({ label, value }))}
      fullWidthLabels={FULL_WIDTH_LABELS}
    />
  )
}

/**
 * Read-only intake values grouped by the same sections as the edit form.
 */
export default function IntakeCategorizedDisplay({ intake, websiteUrl }) {
  if (!intake || typeof intake !== "object") {
    return null
  }

  const resolvedWebsite = websiteUrl || intake.websiteUrl || ""
  const sections = buildPreviewSections(intake, resolvedWebsite)

  return (
    <Column sx={{ gap: 2.5, alignItems: "stretch", width: "100%" }}>
      {sections.map((section) => (
        <RecruitProfileSectionCard
          key={section.title}
          title={section.title}
          subheader={section.subheader}
          useNestedPanel={
            !RECRUIT_PROFILE_SELF_NESTED_TITLES.has(section.title)
          }
        >
          <SectionBody section={section} />
        </RecruitProfileSectionCard>
      ))}
    </Column>
  )
}
